import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/auth.store'
import { useSessionTimeout } from '../hooks/useSessionTimeout'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import ThemeToggle from '../components/ThemeToggle'
import {
  LayoutDashboard,
  Building2,
  Users,
  MessageSquare,
  Shield,
  ScrollText,
  CreditCard,
  Tag,
  Layers,
  BrainCircuit,
  Settings,
  LogOut,
  ShieldCheck,
  ShieldAlert,
  ListChecks,
  BarChart3,
  DollarSign,
  Brain,
  Database,
  TrendingUp,
  GitBranch,
  FileText,
  FlaskConical,
  Activity,
  Package,
} from 'lucide-react'
import clsx from 'clsx'

interface NavItem {
  path: string
  labelKey: string
  icon: React.ReactNode
  roles?: string[]
}

const navItemDefs: NavItem[] = [
  {
    path: '/',
    labelKey: 'nav.dashboard',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    path: '/workspaces',
    labelKey: 'nav.workspaces',
    icon: <Building2 className="w-5 h-5" />,
  },
  {
    path: '/users',
    labelKey: 'nav.users',
    icon: <Users className="w-5 h-5" />,
  },
  {
    path: '/channels',
    labelKey: 'nav.channels',
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    path: '/admins',
    labelKey: 'nav.admins',
    icon: <Shield className="w-5 h-5" />,
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/audit',
    labelKey: 'nav.auditLog',
    icon: <ScrollText className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/billing',
    labelKey: 'nav.billing',
    icon: <CreditCard className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/platform-analytics',
    labelKey: 'nav.platformAnalytics',
    icon: <BarChart3 className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/ai-cost-analytics',
    labelKey: 'nav.aiCostAnalytics',
    icon: <DollarSign className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/hallucination-analytics',
    labelKey: 'nav.hallucinationAnalytics',
    icon: <Brain className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/whatsapp-cost',
    labelKey: 'nav.whatsappCost',
    icon: <MessageSquare className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/pricing',
    labelKey: 'nav.pricing',
    icon: <Tag className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/addon-catalog',
    labelKey: 'nav.addonCatalog',
    icon: <Package className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/features',
    labelKey: 'nav.features',
    icon: <Layers className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/ai-config',
    labelKey: 'nav.aiConfig',
    icon: <BrainCircuit className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/security',
    labelKey: 'nav.security',
    icon: <ShieldAlert className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/queues',
    labelKey: 'nav.queues',
    icon: <ListChecks className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/cache',
    labelKey: 'nav.cache',
    icon: <Database className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/verticals',
    labelKey: 'nav.verticals',
    icon: <TrendingUp className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/ai-decisions',
    labelKey: 'nav.aiDecisions',
    icon: <GitBranch className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/knowledge-base',
    labelKey: 'nav.knowledgeBase',
    icon: <FileText className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING', 'SUPPORT'],
  },
  {
    path: '/experiments',
    labelKey: 'nav.experiments',
    icon: <FlaskConical className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN', 'BILLING'],
  },
  {
    path: '/self-healing',
    labelKey: 'nav.selfHealing',
    icon: <Activity className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/compliance',
    labelKey: 'nav.compliance',
    icon: <ShieldCheck className="w-5 h-5" />,
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
]

function AdminLayout({ children }: { children: React.ReactNode }) {
  const { admin, logout } = useAuthStore()
  const location = useLocation()
  const { t } = useTranslation()

  useSessionTimeout()

  const filteredNavItems = navItemDefs.filter(
    (item) => !item.roles || item.roles.includes(admin?.role || '')
  )

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-gray-900 text-white flex flex-col">
        <div className="flex items-center gap-3 p-4 border-b border-gray-800">
          <Shield className="w-8 h-8 text-primary-400" />
          <div>
            <h1 className="font-bold text-lg">Wakeel Admin</h1>
            <p className="text-xs text-gray-400">
              {t('nav.platformManagement')}
            </p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                (
                  item.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.path)
                )
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800'
              )}
            >
              {item.icon}
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Settings, Language Switcher & Admin info */}
        <div className="p-4 border-t border-gray-800 space-y-2">
          <Link
            to="/settings"
            className={clsx(
              'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
              location.pathname === '/settings'
                ? 'bg-primary-600 text-white'
                : 'text-gray-300 hover:bg-gray-800'
            )}
          >
            <Settings className="w-5 h-5" />
            {t('nav.settings')}
          </Link>

          <LanguageSwitcher />

          <ThemeToggle />

          <div className="flex items-center justify-between px-2 pt-2">
            <div>
              <p className="font-medium text-sm">{admin?.name}</p>
              <p className="text-xs text-gray-400">{admin?.role}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title={t('common.logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">{children}</main>
    </div>
  )
}

export default AdminLayout
